import { ReactNode } from 'react'

import { useSearchParams } from 'react-router-dom'

import EmbedLayout from '@src/layouts/EmbedLayout'
import { ExchangeDataProvider } from '@src/pages/Exchange/context/ExchangeDataContext'
import { ExchangeStateProvider } from '@src/pages/Exchange/context/ExchangeStateContext'

import EmbedPage from '.'

// Helper function to decode URL-encoded hex color
const decodeHexColor = (encodedColor: string | null): string | undefined => {
    if (!encodedColor) return undefined

    try {
        // Validate format after decoding
        const decoded = decodeURIComponent(encodedColor)
        if (/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(decoded)) {
            return decoded
        }
        return undefined
    } catch (error) {
        console.error('Invalid color format:', error)
        return undefined
    }
}

// Helper to parse numeric parameters with fallback values
const parseNumericParam = (param: string | null, fallback?: number): number | undefined => {
    if (!param) return undefined

    const parsedValue = parseInt(param, 10)
    return isNaN(parsedValue) ? fallback : parsedValue
}

// Validate logo position parameter
const validateLogoPosition = (position: string | null): 'left' | 'center' | 'right' | undefined => {
    if (!position) return undefined

    const validPositions = ['left', 'center', 'right']
    const normalizedPosition = position.toLowerCase()

    return validPositions.includes(normalizedPosition) ? (normalizedPosition as 'left' | 'center' | 'right') : undefined
}

// Function to generate complementary colors
const generateComplementaryColors = (
    color: string
): { primary: string; background: string; buttonTextColor: string } => {
    // Remove the # from the beginning
    const hex = color.substring(1)

    // Convert hex to RGB
    const r = parseInt(hex.slice(0, 2), 16)
    const g = parseInt(hex.slice(2, 4), 16)
    const b = parseInt(hex.slice(4, 6), 16)

    if (isNaN(r) || isNaN(g) || isNaN(b)) {
        return { primary: '#155DFC', background: '#131313', buttonTextColor: '#FFFFFF' } // Default fallback
    }

    // Calculate luminance (simplified)
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255

    // Calculate color saturation (simplified)
    const max = Math.max(r, g, b) / 255
    const min = Math.min(r, g, b) / 255
    const saturation = (max - min) / (max || 1) // Avoid division by zero

    // Determine dominant color channel
    const isDominantR = r > g && r > b
    const isDominantG = g > r && g > b
    const isDominantB = b > r && b > g

    // Special case for purplish colors (high red and blue)
    const isPurplish = r > 100 && b > 100 && g < Math.min(r, b)

    // Calculate button text color
    const getButtonTextColor = (primaryR: number, primaryG: number, primaryB: number): string => {
        // Calculate luminance of the primary color
        const primaryLuminance = (0.299 * primaryR + 0.587 * primaryG + 0.114 * primaryB) / 255
        // Use white text for dark backgrounds, black text for light backgrounds
        return primaryLuminance < 0.5 ? '#FFFFFF' : '#000000'
    }

    if (isPurplish) {
        // For purple backgrounds, create a lighter purple or complementary color
        // A light lavender or cyan often works well with purple
        if (luminance < 0.4) {
            // For dark purples, create a light lavender
            const primary = '#E0B0FF' // Light lavender
            // Calculate text color for this primary
            const textColor = getButtonTextColor(224, 176, 255) // RGB for #E0B0FF

            return {
                primary,
                background: color,
                buttonTextColor: textColor,
            }
        } else {
            // For light purples, create a deep purple
            const primary = '#4B0082' // Indigo
            // Calculate text color for this primary
            const textColor = getButtonTextColor(75, 0, 130) // RGB for #4B0082

            return {
                primary,
                background: color,
                buttonTextColor: textColor,
            }
        }
    } else if (luminance > 0.7) {
        // Very light color - create darker background and darker primary
        // For very light backgrounds, derive a primary color based on the hue
        let primaryR = r
        let primaryG = g
        let primaryB = b

        if (isDominantR) primaryR = Math.min(255, r + 40)
        if (isDominantG) primaryG = Math.min(255, g + 40)
        if (isDominantB) primaryB = Math.min(255, b + 40)

        // Ensure the primary is vibrant enough
        const minSaturation = 0.5
        const maxChannel = Math.max(primaryR, primaryG, primaryB)

        if (saturation < minSaturation && maxChannel > 0) {
            const saturationBoost = minSaturation / (saturation || 0.1)
            primaryR = Math.min(255, primaryR * saturationBoost)
            primaryG = Math.min(255, primaryG * saturationBoost)
            primaryB = Math.min(255, primaryB * saturationBoost)
        }

        const primary = `#${Math.round(primaryR).toString(16).padStart(2, '0')}${Math.round(primaryG).toString(16).padStart(2, '0')}${Math.round(primaryB).toString(16).padStart(2, '0')}`
        const textColor = getButtonTextColor(primaryR, primaryG, primaryB)

        return {
            primary,
            background: color,
            buttonTextColor: textColor,
        }
    } else if (luminance < 0.3) {
        // Dark color background - create lighter or contrasting primary
        // For dark backgrounds, we need a brighter, more vibrant primary

        // Boost saturation and value of the color for the primary
        let primaryR = r
        let primaryG = g
        let primaryB = b

        // Identify the dominant channel and boost it
        if (isDominantR) {
            primaryR = Math.min(255, r * 2)
            primaryG = Math.min(255, g * 0.8)
            primaryB = Math.min(255, b * 0.8)
        } else if (isDominantG) {
            primaryR = Math.min(255, r * 0.8)
            primaryG = Math.min(255, g * 2)
            primaryB = Math.min(255, b * 0.8)
        } else if (isDominantB) {
            primaryR = Math.min(255, r * 0.8)
            primaryG = Math.min(255, g * 0.8)
            primaryB = Math.min(255, b * 2)
        } else {
            // No dominant channel - boost all for a whiter primary
            primaryR = Math.min(255, r + 150)
            primaryG = Math.min(255, g + 150)
            primaryB = Math.min(255, b + 150)
        }

        const primary = `#${Math.round(primaryR).toString(16).padStart(2, '0')}${Math.round(primaryG).toString(16).padStart(2, '0')}${Math.round(primaryB).toString(16).padStart(2, '0')}`
        const textColor = getButtonTextColor(primaryR, primaryG, primaryB)

        return {
            primary,
            background: color,
            buttonTextColor: textColor,
        }
    } else {
        // Medium luminance - create complementary color
        // Find if it's more reddish, greenish, or bluish and create contrast
        let primaryR = 255 - r
        let primaryG = 255 - g
        let primaryB = 255 - b

        // Boost the primary's saturation
        const boost = 1.3
        primaryR = Math.min(255, primaryR * boost)
        primaryG = Math.min(255, primaryG * boost)
        primaryB = Math.min(255, primaryB * boost)

        const primary = `#${Math.round(primaryR).toString(16).padStart(2, '0')}${Math.round(primaryG).toString(16).padStart(2, '0')}${Math.round(primaryB).toString(16).padStart(2, '0')}`
        const textColor = getButtonTextColor(primaryR, primaryG, primaryB)

        return {
            primary,
            background: color,
            buttonTextColor: textColor,
        }
    }
}

interface EmbedWrapperProps {
    children?: ReactNode
}

const EmbedWrapper = ({ children }: EmbedWrapperProps = {}) => {
    const [searchParams] = useSearchParams()

    // Get styling configuration from URL params
    const themePrimaryParam = searchParams.get('themePrimary')
    const themeBackgroundParam = searchParams.get('themeBackground')
    const themeModalBackgroundParam = searchParams.get('themeModalBackground')
    const logoUrlParam = searchParams.get('logoUrl')
    const showBrandingParam = searchParams.get('showBranding')
    const hideAIInputParam = searchParams.get('hideAIInput')
    const useIntuitiveColorDefaultsParam = searchParams.get('useIntuitiveColorDefaults')

    // Get logo customization parameters
    const logoHeightParam = searchParams.get('logoHeight')
    const logoWidthParam = searchParams.get('logoWidth')
    const logoPositionParam = searchParams.get('logoPosition')

    // Get pending transaction indicator customization parameters
    const showPendingTXParam = searchParams.get('showPendingTX')
    const pendingTXPositionParam = searchParams.get('pendingTXPosition')

    // Get hamburger menu parameter
    const showHamburgerMenuParam = searchParams.get('showHamburgerMenu')

    // Get token and chain configuration from URL params
    const fromParam = searchParams.get('from')
    const toParam = searchParams.get('to')
    const chainsParam = searchParams.get('chains')
    const destinationWalletParam = searchParams.get('destinationWallet')
    const recipientAddressParam = searchParams.get('recipientAddress') // Legacy parameter

    // Parse URL parameters
    const decodedPrimaryColor = decodeHexColor(themePrimaryParam)
    const decodedBackgroundColor = decodeHexColor(themeBackgroundParam)
    const decodedModalBackgroundColor = decodeHexColor(themeModalBackgroundParam)
    const useIntuitiveDefaults = useIntuitiveColorDefaultsParam === 'true'

    // Apply intuitive defaults if only one color is provided and explicitly enabled
    let primaryColor = decodedPrimaryColor
    let backgroundColor = decodedBackgroundColor
    let modalBackgroundColor = decodedModalBackgroundColor
    let buttonTextColor = '#FFFFFF' // Default to white text

    if (useIntuitiveDefaults) {
        if (primaryColor && !backgroundColor) {
            // Only primary color provided, derive complementary background
            const complementaryColors = generateComplementaryColors(primaryColor)
            backgroundColor = complementaryColors.background
            buttonTextColor = complementaryColors.buttonTextColor

            // If modal background not specified, darken the main background slightly
            if (!modalBackgroundColor) {
                const bgHex = backgroundColor.substring(1)
                const r = parseInt(bgHex.slice(0, 2), 16)
                const g = parseInt(bgHex.slice(2, 4), 16)
                const b = parseInt(bgHex.slice(4, 6), 16)

                // Darken by 30%
                const darkenFactor = 0.7
                const modalR = Math.max(0, Math.floor(r * darkenFactor))
                const modalG = Math.max(0, Math.floor(g * darkenFactor))
                const modalB = Math.max(0, Math.floor(b * darkenFactor))

                modalBackgroundColor = `#${modalR.toString(16).padStart(2, '0')}${modalG.toString(16).padStart(2, '0')}${modalB.toString(16).padStart(2, '0')}`
            }
        } else if (!primaryColor && backgroundColor) {
            // Only background color provided, derive complementary primary
            const complementaryColors = generateComplementaryColors(backgroundColor)
            primaryColor = complementaryColors.primary
            buttonTextColor = complementaryColors.buttonTextColor

            // If modal background not specified, darken the main background slightly
            if (!modalBackgroundColor) {
                const bgHex = backgroundColor.substring(1)
                const r = parseInt(bgHex.slice(0, 2), 16)
                const g = parseInt(bgHex.slice(2, 4), 16)
                const b = parseInt(bgHex.slice(4, 6), 16)

                // Darken by 30%
                const darkenFactor = 0.7
                const modalR = Math.max(0, Math.floor(r * darkenFactor))
                const modalG = Math.max(0, Math.floor(g * darkenFactor))
                const modalB = Math.max(0, Math.floor(b * darkenFactor))

                modalBackgroundColor = `#${modalR.toString(16).padStart(2, '0')}${modalG.toString(16).padStart(2, '0')}${modalB.toString(16).padStart(2, '0')}`
            }
        }
    }

    // Always calculate button text color and modal background if needed, regardless of useIntuitiveDefaults
    if (primaryColor && !buttonTextColor) {
        // Calculate text color for buttons based on primary color
        const hex = primaryColor.substring(1)
        const r = parseInt(hex.slice(0, 2), 16)
        const g = parseInt(hex.slice(2, 4), 16)
        const b = parseInt(hex.slice(4, 6), 16)

        if (!isNaN(r) && !isNaN(g) && !isNaN(b)) {
            const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
            buttonTextColor = luminance < 0.5 ? '#FFFFFF' : '#000000'
        }
    }

    // Only derive modal background from main background if useIntuitiveDefaults is enabled
    if (useIntuitiveDefaults && backgroundColor && !modalBackgroundColor) {
        const bgHex = backgroundColor.substring(1)
        const r = parseInt(bgHex.slice(0, 2), 16)
        const g = parseInt(bgHex.slice(2, 4), 16)
        const b = parseInt(bgHex.slice(4, 6), 16)

        if (!isNaN(r) && !isNaN(g) && !isNaN(b)) {
            // Darken by 30%
            const darkenFactor = 0.7
            const modalR = Math.max(0, Math.floor(r * darkenFactor))
            const modalG = Math.max(0, Math.floor(g * darkenFactor))
            const modalB = Math.max(0, Math.floor(b * darkenFactor))

            modalBackgroundColor = `#${modalR.toString(16).padStart(2, '0')}${modalG.toString(16).padStart(2, '0')}${modalB.toString(16).padStart(2, '0')}`
        }
    }

    const logoUrl = logoUrlParam ? decodeURIComponent(logoUrlParam) : undefined
    const showBranding = showBrandingParam !== 'false'
    const hideAIInput = hideAIInputParam === 'true'

    // Parse logo customization parameters
    const logoHeight = parseNumericParam(logoHeightParam)
    const logoWidth = parseNumericParam(logoWidthParam)
    const logoPosition = validateLogoPosition(logoPositionParam)

    // Parse pending transaction parameters
    const showPendingTX = showPendingTXParam === 'true'
    const pendingTXPosition = validateLogoPosition(pendingTXPositionParam) // Reuse the same validation

    // Parse hamburger menu parameter
    const showHamburgerMenu = showHamburgerMenuParam === 'true'

    return (
        <EmbedLayout
            primaryColor={primaryColor}
            backgroundColor={backgroundColor}
            modalBackgroundColor={modalBackgroundColor}
            buttonTextColor={buttonTextColor}
            logoUrl={logoUrl}
            showBranding={showBranding}
            logoHeight={logoHeight}
            logoWidth={logoWidth}
            logoPosition={logoPosition}
            showPendingTX={showPendingTX}
            pendingTXPosition={pendingTXPosition}
            showHamburgerMenu={showHamburgerMenu}
            useIntuitiveColorDefaults={useIntuitiveDefaults}
        >
            {children || (
                <ExchangeStateProvider>
                    <ExchangeDataProvider>
                        <EmbedPage
                            hideAIInput={hideAIInput}
                            fromParam={fromParam}
                            toParam={toParam}
                            chainsParam={chainsParam}
                            destinationWalletParam={destinationWalletParam || recipientAddressParam}
                        />
                    </ExchangeDataProvider>
                </ExchangeStateProvider>
            )}
        </EmbedLayout>
    )
}

export default EmbedWrapper
